import { cn } from "@/lib/utils";

export type ActiveFiltersBarStripTone = "idle" | "hiddenFilter";

/** Tokens for the active-filters bar (header) plus the query-table footer badge (Popover/Sheet trigger). */
export interface ActiveFiltersBarToneArtifacts {
  barToneClass: string;
  dividerClass: string;
  idleLabelClass: string;
  triggerInteractionsClass: string;
  activeGlyphClass: string;
  footerBadgeSurfaceClass: string;
  footerBadgeHoverClass: string;
  footerBadgeFocusRingOffsetClass: string;
  footerBadgeVerticalDividerClass: string;
  footerBadgeSegmentMutedClass: string;
}

/**
 * Two visual tones for the active-filters bar and query-table footer badge:
 * - `idle`: neutral styling when there is no committed filter on a hidden/invalid column
 *   (filters on visible columns, active sorting, and in-progress filter UI still use this tone).
 * - `hiddenFilter`: attention (yellow) when at least one committed filter targets a hidden or invalid column.
 */
export const resolveActiveFiltersStripTone = ({
  hasHiddenCommittedFilter,
}: {
  hasHiddenCommittedFilter: boolean;
}): ActiveFiltersBarStripTone =>
  hasHiddenCommittedFilter ? "hiddenFilter" : "idle";

/** Shared row height for filter/sort segments so idle vs active doesn’t jump layout. */
export const ACTIVE_FILTERS_BAR_SEGMENT_CLASS =
  "inline-flex h-8 min-h-8 max-w-full items-center gap-1.5 leading-none max-sm:shrink-0 max-sm:whitespace-nowrap";

/**
 * Horizontal inset + rounding shared by idle `<span>` and triggers so swapping to `<button>`
 * does not jog the bar layout.
 */
export const ACTIVE_FILTERS_BAR_SEGMENT_INNER_PAD_CLASS = "rounded-md px-1.5";

/** Layout + alignment for footer badge when composed with `@/components/ui/button` (“outline” tonal overrides). */
export const QUERY_TABLE_FS_BADGE_TRIGGER_LAYOUT_CLASS = cn(
  "h-auto min-h-[2rem] w-max max-w-[min(100%,24rem)] shrink-0 flex-nowrap gap-2 px-3 py-2 text-left font-normal shadow-sm",
  "[&_svg]:h-3.5 [&_svg]:w-3.5",
);

/** Bordered shell for active-filter rows (`ActiveFiltersSummaryList`). */
export const ACTIVE_FILTERS_PANEL_SHELL_CLASS =
  "rounded-md border border-border/50 bg-muted/10";

export function activeFiltersBarToneClasses(
  tone: ActiveFiltersBarStripTone,
): ActiveFiltersBarToneArtifacts {
  switch (tone) {
    case "hiddenFilter":
      return {
        barToneClass:
          "border-yellow-300/70 bg-yellow-50 text-yellow-950 dark:border-yellow-800/55 dark:bg-yellow-950/45 dark:text-yellow-50/95",
        dividerClass: "bg-yellow-500/40 dark:bg-yellow-600/45",
        idleLabelClass: "text-yellow-900/72 dark:text-yellow-100/82",
        triggerInteractionsClass: cn(
          "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "ring-offset-yellow-50 dark:ring-offset-yellow-950",
          "hover:bg-yellow-950/10 dark:hover:bg-white/11",
        ),
        activeGlyphClass: "text-yellow-900/58 dark:text-yellow-300/90",
        footerBadgeSurfaceClass:
          "border-yellow-300/70 bg-yellow-50 text-yellow-950 dark:border-yellow-800/55 dark:bg-yellow-950/45 dark:text-yellow-50/95",
        footerBadgeHoverClass:
          "hover:bg-yellow-100/90 dark:hover:bg-yellow-950/52",
        footerBadgeFocusRingOffsetClass:
          "ring-offset-yellow-50 dark:ring-offset-yellow-950",
        footerBadgeVerticalDividerClass:
          "bg-yellow-500/40 dark:bg-yellow-600/45",
        footerBadgeSegmentMutedClass:
          "text-yellow-900/68 dark:text-yellow-200/82",
      };
    default:
      return {
        barToneClass:
          "border-slate-200 bg-slate-100 text-slate-800 dark:border-slate-700 dark:bg-slate-900/85 dark:text-slate-100",
        dividerClass: "bg-slate-300/70 dark:bg-slate-600/50",
        idleLabelClass: "text-slate-600 dark:text-slate-400",
        triggerInteractionsClass: cn(
          "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "ring-offset-slate-100 dark:ring-offset-slate-900",
          "hover:bg-slate-950/10 dark:hover:bg-white/11",
        ),
        activeGlyphClass: "text-slate-700/80 dark:text-slate-300/95",
        footerBadgeSurfaceClass:
          "border-slate-200 bg-slate-100 text-slate-900 dark:border-slate-600 dark:bg-slate-900/90 dark:text-slate-50",
        footerBadgeHoverClass:
          "hover:bg-slate-200/90 dark:hover:bg-slate-800/90",
        footerBadgeFocusRingOffsetClass:
          "ring-offset-slate-100 dark:ring-offset-slate-900",
        footerBadgeVerticalDividerClass: "bg-slate-300/65 dark:bg-slate-600/55",
        footerBadgeSegmentMutedClass:
          "text-slate-800/72 dark:text-slate-200/82",
      };
  }
}
